import { Box, Table, Text } from '@mantine/core'
import type { ChargeQuoteResult } from './useSubmittedChargeQuote'
import { reasonCopy } from './chargeQuoteCopy'

const money = (value: number | null) => (value === null ? '—' : `$${value.toFixed(2)}`)

/** How the Submitted Charge was derived for one billing entry (PD-1597). */
export function SubmittedChargeBreakdown({
  quote,
  code,
}: {
  quote: ChargeQuoteResult
  code: string
}) {
  const line = quote.quotes.find((q) => q.code === code)
  if (!line) return null
  const provider = `${quote.provider.display ?? 'Rendering provider'}${
    quote.provider.credential ? `, ${quote.provider.credential}` : ''
  }`
  const rows: [string, string][] = [
    ['Rendering provider', provider],
    ['Insurance category', quote.insuranceCategory ?? 'Not set — attach a plan on the coverage'],
    ['Code', line.code],
    ['MD/DO base charge', money(line.basePrice)],
    ['Applied provider %', line.percent === null ? '—' : `${line.percent}%`],
    ['Submitted charge', money(line.submittedCharge)],
    ['Expected reimbursement', 'Unavailable — contract rate not configured'],
    ['Estimated patient responsibility', 'Unavailable'],
  ]
  return (
    <Box mt="xs" data-testid="submitted-charge-breakdown">
      <Table withRowBorders={false} verticalSpacing={2} fz="xs">
        <Table.Tbody>
          {rows.map(([label, value]) => (
            <Table.Tr key={label}>
              <Table.Td c="dimmed" w="45%">
                {label}
              </Table.Td>
              <Table.Td>{value}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {line.reason && (
        <Text size="xs" c="orange.8" mt={2}>
          {reasonCopy(line)}
        </Text>
      )}
    </Box>
  )
}
